import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { ItemService } from '../../services/item.service';
import { GoodsReceipt, GoodsReceiptStatus } from '../../interfaces/procurement/goodsReceipt.interface';
import { Item } from '../../interfaces/item.interface';
import { ReverseConfirmDialog } from './ReverseConfirmDialog';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS: Record<GoodsReceiptStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  POSTED: 'success',
  REVERSED: 'error',
};

export const GoodsReceiptDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [grn, setGrn] = useState<GoodsReceipt | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reversing, setReversing] = useState(false);

  const itemMap = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  useEffect(() => {
    if (!id) return;
    Promise.all([
      GoodsReceiptService.getById(id),
      ItemService.getItemsByInstitution(0, 500),
    ])
      .then(([g, itemsRes]) => {
        setGrn(g);
        setItems(itemsRes.content);
      })
      .catch(e => toast.showApiError(e, 'Failed to load goods receipt'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePost = async () => {
    if (!grn) return;
    const confirmed = window.confirm('Post this GRN? Stock will be added and a journal entry will be created.');
    if (!confirmed) return;
    setPosting(true);
    try {
      const updated = await GoodsReceiptService.post(grn.id);
      setGrn(updated);
      toast.showSuccess('GRN posted successfully');
    } catch (e) {
      toast.showApiError(e, 'Failed to post GRN');
    } finally {
      setPosting(false);
    }
  };

  const handleReverse = async () => {
    if (!grn) return;
    setReversing(true);
    try {
      const updated = await GoodsReceiptService.reverse(grn.id);
      setGrn(updated);
      toast.showSuccess('GRN reversed');
      setReverseDialogOpen(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to reverse GRN');
    } finally {
      setReversing(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!grn) return <Box sx={{ p: 3 }}><Typography>Goods receipt not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">{grn.grnNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {grn.status === 'DRAFT' && (
            <Button variant="contained" color="primary" onClick={handlePost} disabled={posting}>
              {posting ? <CircularProgress size={20} /> : 'Post'}
            </Button>
          )}
          {grn.status === 'POSTED' && (
            <Button variant="outlined" color="error" onClick={() => setReverseDialogOpen(true)}>
              Reverse
            </Button>
          )}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">GRN Number</Typography>
            <Typography>{grn.grnNumber}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip label={grn.status} color={STATUS_COLORS[grn.status]} size="small" />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Purchase Order ID</Typography>
            <Typography>{grn.purchaseOrderId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Store ID</Typography>
            <Typography>{grn.storeId}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Receipt Date</Typography>
            <Typography>{grn.receiptDate}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total Value</Typography>
            <Typography>{grn.totalValue}</Typography>
          </Box>
          {grn.notes && (
            <Box sx={{ gridColumn: 'span 2' }}>
              <Typography variant="body2" color="text.secondary">Notes</Typography>
              <Typography>{grn.notes}</Typography>
            </Box>
          )}
          {grn.status === 'REVERSED' && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed By</Typography>
                <Typography>{grn.reversedById ?? '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Reversed Date</Typography>
                <Typography>{grn.reversedDate ?? '-'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Line ID</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Received Qty</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Line Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grn.lines.map(line => (
              <TableRow key={line.id}>
                <TableCell>{line.id}</TableCell>
                <TableCell>{itemMap.get(line.itemId)?.itemName ?? line.itemId}</TableCell>
                <TableCell>{line.receivedQty}</TableCell>
                <TableCell>{line.unitPrice}</TableCell>
                <TableCell>{line.lineValue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <ReverseConfirmDialog
        open={reverseDialogOpen}
        entityLabel="Goods Receipt"
        consequence="Stock will be reversed and a reversing journal entry posted."
        onCancel={() => setReverseDialogOpen(false)}
        onConfirm={handleReverse}
        loading={reversing}
      />
    </Box>
  );
};

export default GoodsReceiptDetail;
